/**
 * L-3 Cost Inbox — unassigned supplier invoices land here for job costing.
 *
 * Admin-only. Drop one or many invoices (PDF / photos); the AI reads each one
 * and pre-fills supplier, total and suggested lines. "Process" opens a
 * full-screen review: zoomable invoice on the left, editable lines on the
 * right. Posting writes one CONFIRMED Cost Ledger row per line. Invoices can
 * be put On hold (re-openable) or, discreetly, deleted (statements / mistakes).
 */
import { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useCompanyLens } from '../hooks/useCompanyLens';
import { toast } from 'sonner';
import { Loader2, Inbox, Landmark, Link2, ArrowLeftRight, FolderPlus, Send, Mail } from 'lucide-react';
import { InboxDropZone } from '../components/cost-inbox/InboxDropZone';
import { InboxRow } from '../components/cost-inbox/InboxRow';
import { EmptyBinButton } from '../components/cost-inbox/InboxActions';
import { ProcessDialog } from '../components/cost-inbox/ProcessDialog';
import { AdhocProjectsDialog } from '../components/cost-inbox/AdhocProjectsDialog';
import { isReading } from '../components/cost-inbox/inboxFormat';

const FILTERS = [
  { key: 'active', label: 'Active' },
  { key: 'new', label: 'New' },
  { key: 'in_review', label: 'In review' },
  { key: 'on_hold', label: 'On hold' },
  { key: 'no_project', label: 'No project' },
  { key: 'queries', label: 'Queries' },
  { key: 'cis', label: 'CIS' },
  { key: 'posted', label: 'Posted' },
  { key: 'statement', label: 'Statements' },
  { key: 'split', label: 'Split', onlyWhenCounted: true },
  { key: 'deleted', label: 'Deleted' },
];

const COMPANY_LENS = [
  { key: 'all', label: 'Global', active: 'bg-[#F5A623] text-[#1E2530] border-[#F5A623]' },
  { key: 'DEV', label: 'LUX Developments', active: 'bg-[#F5A623] text-[#1E2530] border-[#F5A623]' },
  { key: 'CON', label: 'LUX Construction', active: 'bg-sky-500 text-white border-sky-500' },
];

// Which company an inbox item belongs to: the billed-to company once set,
// otherwise the majority of its AI-assigned jobs. Unknown (nothing assigned
// yet) returns null so it shows in every lens — an invoice is never hidden.
const companyForItem = (it) => {
  if (it.billed_to_company === 'DEV' || it.billed_to_company === 'CON') return it.billed_to_company;
  const refs = (it.lines || []).map(l => l.project_ref).filter(Boolean);
  if (!refs.length) return null;
  const con = refs.filter(r => String(r).toUpperCase().startsWith('LC')).length;
  return con > refs.length - con ? 'CON' : 'DEV';
};

export default function CostInbox() {
  const { api, user, isAdmin } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (!user) return;
    if (!isAdmin) navigate('/projects');
  }, [user, isAdmin, navigate]);

  const [items, setItems] = useState([]);
  const [counts, setCounts] = useState({});
  const [activeTotal, setActiveTotal] = useState(0);
  const [filter, setFilter] = useState('active');
  const [loading, setLoading] = useState(true);
  const [projects, setProjects] = useState([]);
  const [openId, setOpenId] = useState(null);
  const [openEdit, setOpenEdit] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();
  // Deep link from the CIS ledger / Cost Ledger: /cost-inbox?open=<item id>[&edit=1]
  useEffect(() => {
    const id = searchParams.get('open');
    if (id) { setOpenId(id); setOpenEdit(searchParams.get('edit') === '1'); setSearchParams({}, { replace: true }); }
  }, [searchParams, setSearchParams]);
  const [adhocOpen, setAdhocOpen] = useState(false);
  const [retentionDays, setRetentionDays] = useState(30);
  const [matchCount, setMatchCount] = useState(0);
  const [rebillCount, setRebillCount] = useState(0);
  const [companyView, setCompanyView] = useCompanyLens();
  const [statementSupplier, setStatementSupplier] = useState('all');
  useEffect(() => { setStatementSupplier('all'); }, [filter]);

  const lensItems = companyView === 'all'
    ? items
    : items.filter(it => { const c = companyForItem(it); return c === null || c === companyView; });
  // Statements folder: group by supplier, filter to one supplier or all.
  const statementSuppliers = filter === 'statement'
    ? Object.entries(lensItems.reduce((m, it) => { const k = (it.supplier_name || 'Unknown supplier').trim(); m[k] = (m[k] || 0) + 1; return m; }, {})).sort((a, b) => a[0].localeCompare(b[0]))
    : [];
  const visibleItems = filter === 'statement' && statementSupplier !== 'all'
    ? lensItems.filter(it => (it.supplier_name || 'Unknown supplier').trim() === statementSupplier)
    : lensItems;

  useEffect(() => {
    if (!isAdmin) return;
    api.get('/cost-ledger/match-queue/summary').then(r => setMatchCount(r.data?.total || 0)).catch(() => {});
    api.get('/cost-ledger/rebill/summary').then(r => setRebillCount(r.data?.invoice_count || 0)).catch(() => {});
  }, [api, isAdmin]);

  const load = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      const qs = filter === 'no_project' ? '?status=on_hold&no_project=true'
        : filter === 'queries' ? '?query=open'
        : filter === 'cis' ? '?cis=true'
        : filter === 'active' ? '' : `?status=${filter}`;
      const r = await api.get(`/cost-inbox${qs}`);
      setItems(r.data.items || []);
      setCounts(r.data.counts || {});
      setActiveTotal(r.data.active_total || 0);
      if (r.data.deleted_retention_days) setRetentionDays(r.data.deleted_retention_days);
    } catch (e) {
      if (!silent) toast.error(e.response?.data?.detail || 'Failed to load inbox');
    } finally { if (!silent) setLoading(false); }
  }, [api, filter]);

  useEffect(() => { if (isAdmin) load(); }, [isAdmin, load]);
  const loadProjects = useCallback(() => { api.get('/projects?limit=200').then(r => setProjects(r.data || [])).catch(() => {}); }, [api]);
  useEffect(() => { loadProjects(); }, [loadProjects]);

  const reading = items.some(isReading);
  useEffect(() => {
    if (!reading) return undefined;
    const t = setTimeout(() => load(true), 3000);
    return () => clearTimeout(t);
  }, [reading, items, load]);

  const onUploaded = useCallback((n) => {
    toast.success(`${n} invoice${n === 1 ? '' : 's'} added — AI is reading ${n === 1 ? 'it' : 'them'} now`);
    if (filter !== 'active' && filter !== 'new') setFilter('active');
    else load(true);
  }, [filter, load]);

  return (
    <div className="min-h-screen bg-[#1E2530] text-white p-6" data-testid="cost-inbox-page">
      <div className="max-w-6xl mx-auto">
        <div className="mb-5 flex items-start justify-between gap-3 flex-wrap">
          <div>
            <h1 className="text-3xl font-bold flex items-center gap-2">
              <Inbox className="w-7 h-7 text-[#F5A623]" /> Cost Inbox
            </h1>
            <p className="text-sm text-gray-400 mt-1">Supplier invoices land here. Check the AI read, split across jobs if needed, and post to the cost ledger.</p>
          </div>
          <div className="flex items-center gap-2">
            <button type="button" onClick={() => setAdhocOpen(true)} className="inline-flex items-center gap-1.5 text-sm px-3 py-2 rounded-lg border border-white/15 bg-white/5 text-gray-200 hover:bg-white/10" data-testid="inbox-open-adhoc">
              <FolderPlus className="w-4 h-4 text-sky-300" /> Ad-hoc jobs
            </button>
            <button type="button" onClick={() => navigate('/cost-ledger?rebill=1')} className="relative inline-flex items-center gap-1.5 text-sm px-3 py-2 rounded-lg border border-white/15 bg-white/5 text-gray-200 hover:bg-white/10" data-testid="inbox-open-rebill-review">
              <ArrowLeftRight className="w-4 h-4 text-purple-300" /> Re-bill
              {rebillCount > 0 && <span className="ml-1 inline-flex items-center justify-center min-w-[18px] h-[18px] text-[10px] font-semibold rounded-full bg-purple-500 text-white px-1" data-testid="inbox-rebill-count">{rebillCount}</span>}
            </button>
            <button type="button" onClick={() => navigate('/cost-ledger/matches')} className="relative inline-flex items-center gap-1.5 text-sm px-3 py-2 rounded-lg border border-white/15 bg-white/5 text-gray-200 hover:bg-white/10" data-testid="inbox-open-match-review">
              <Link2 className="w-4 h-4 text-[#F5A623]" /> Match review
              {matchCount > 0 && <span className="ml-1 inline-flex items-center justify-center min-w-[18px] h-[18px] text-[10px] font-semibold rounded-full bg-[#F5A623] text-[#1E2530] px-1" data-testid="inbox-match-count">{matchCount}</span>}
            </button>
            <button type="button" onClick={() => navigate('/cis-ledger')} className="inline-flex items-center gap-1.5 text-sm px-3 py-2 rounded-lg border border-white/15 bg-white/5 text-gray-200 hover:bg-white/10" data-testid="inbox-open-cis-ledger">
              <Landmark className="w-4 h-4 text-[#F5A623]" /> CIS ledger
            </button>
            <button type="button" onClick={() => navigate('/cost-inbox/sends')} className="inline-flex items-center gap-1.5 text-sm px-3 py-2 rounded-lg border border-white/15 bg-white/5 text-gray-200 hover:bg-white/10" data-testid="inbox-open-sends">
              <Send className="w-4 h-4 text-emerald-300" /> Sends
            </button>
            <button type="button" onClick={() => navigate('/cost-inbox/email-in')} className="inline-flex items-center gap-1.5 text-sm px-3 py-2 rounded-lg border border-white/15 bg-white/5 text-gray-200 hover:bg-white/10" data-testid="inbox-open-email-in">
              <Mail className="w-4 h-4 text-sky-300" /> Email-in
            </button>
          </div>
        </div>

        <InboxDropZone api={api} onUploaded={onUploaded} />

        {/* Company lens — shared with Projects & Cost Ledger */}
        <div className="mt-4 mb-3 flex flex-wrap items-center gap-2" data-testid="inbox-company-view">
          <span className="text-xs uppercase tracking-wider text-gray-500 mr-1">Viewing</span>
          {COMPANY_LENS.map(opt => (
            <button
              key={opt.key}
              type="button"
              onClick={() => setCompanyView(opt.key)}
              className={`h-9 px-4 rounded-md text-sm border transition-colors ${companyView === opt.key ? `${opt.active} font-semibold` : 'bg-[#2A3240] text-gray-300 border-white/15 hover:bg-white/5'}`}
              data-testid={`inbox-company-${opt.key}`}
            >
              {opt.label}
            </button>
          ))}
          {companyView !== 'all' && (
            <span className="text-[11px] text-gray-500">Unassigned invoices stay visible until you set a job</span>
          )}
        </div>

        <div className="flex flex-wrap gap-2 mb-4">
          {FILTERS.map(f => {
            const n = f.key === 'active' ? activeTotal : (counts[f.key] || 0);
            if (f.onlyWhenCounted && !n && filter !== f.key) return null;
            return (
              <button key={f.key} onClick={() => setFilter(f.key)}
                className={`px-3 py-1.5 rounded-full text-sm border transition-colors ${filter === f.key ? 'bg-[#F5A623] text-[#1E2530] border-[#F5A623] font-semibold' : 'bg-white/5 text-gray-300 border-white/10 hover:bg-white/10'}`}
                data-testid={`inbox-filter-${f.key}`}>
                {f.label} <span className="opacity-70">({n})</span>
              </button>
            );
          })}
        </div>

        {filter === 'statement' && (
          <div className="mb-4 bg-violet-500/5 border border-violet-400/20 rounded-lg px-3 py-2 space-y-2" data-testid="inbox-statement-folder">
            <div className="text-xs text-gray-400">Supplier statements filed from the inbox — kept for reference, never posted as costs. Open one to view it or send it back to the inbox.</div>
            {statementSuppliers.length > 0 && (
              <div className="flex flex-wrap gap-1.5" data-testid="inbox-statement-suppliers">
                <button type="button" onClick={() => setStatementSupplier('all')} className={`px-2.5 py-1 rounded-full text-xs border ${statementSupplier === 'all' ? 'bg-violet-500 text-white border-violet-500 font-semibold' : 'bg-white/5 text-gray-300 border-white/10 hover:bg-white/10'}`} data-testid="inbox-statement-supplier-all">All ({lensItems.length})</button>
                {statementSuppliers.map(([name, n]) => (
                  <button key={name} type="button" onClick={() => setStatementSupplier(name)} className={`px-2.5 py-1 rounded-full text-xs border ${statementSupplier === name ? 'bg-violet-500 text-white border-violet-500 font-semibold' : 'bg-white/5 text-gray-300 border-white/10 hover:bg-white/10'}`} data-testid={`inbox-statement-supplier-${name.replace(/\s+/g, '-').toLowerCase()}`}>{name} ({n})</button>
                ))}
              </div>
            )}
          </div>
        )}

        {filter === 'deleted' && (
          <div className="mb-4 flex items-center justify-between gap-3 bg-white/5 border border-white/10 rounded-lg px-3 py-2" data-testid="inbox-deleted-hint">
            <span className="text-xs text-gray-400">Deleted invoices stay here for {retentionDays} days so you can undo a mistake, then they're removed for good. Invoices marked <b className="text-red-300">Voided</b> were posted and then voided — their ledger rows are void and they can't be restored; split invoices can't be restored either.</span>
            <EmptyBinButton api={api} count={counts.deleted || 0} onEmptied={() => load(true)} />
          </div>
        )}

        {loading ? (
          <div className="text-center py-16 text-gray-400"><Loader2 className="w-6 h-6 animate-spin mx-auto" /></div>
        ) : visibleItems.length === 0 ? (
          <div className="text-center py-16 text-gray-500" data-testid="inbox-empty">
            {filter === 'deleted'
              ? 'The bin is empty — deleted invoices show up here for a short while.'
              : (items.length > 0 && companyView !== 'all')
                ? `No ${companyView === 'DEV' ? 'LUX Developments' : 'LUX Construction'} invoices here — switch to Global to see all.`
                : 'No invoices here. Drop some above to get started.'}
          </div>
        ) : (
          <div className="space-y-2" data-testid="inbox-list">
            {visibleItems.map(it => <InboxRow key={it.id} it={it} api={api} onOpen={(x, opts) => { setOpenEdit(!!opts?.edit); setOpenId(x.id); }} onDeleted={() => load(true)} onRestore={() => load(true)} onPurge={() => load(true)} onRefresh={() => load(true)} />)}
          </div>
        )}
      </div>

      {openId && (
        <ProcessDialog api={api} itemId={openId} projects={projects} startEditing={openEdit}
          onClose={() => { setOpenId(null); setOpenEdit(false); }}
          onChanged={() => load(true)}
          onOpenItem={(id) => { setOpenEdit(false); setOpenId(null); setTimeout(() => setOpenId(id), 0); }}
          reloadProjects={loadProjects} />
      )}

      <AdhocProjectsDialog api={api} open={adhocOpen} onOpenChange={setAdhocOpen}
        onChanged={() => { loadProjects(); load(true); }} />
    </div>
  );
}
